import { useState, useEffect, useCallback } from "react";
import {
  getCustomers,
  updateCustomerStatus,
  deleteCustomer,
  Customer,
} from "../services/customers";

export const getStatusPriority = (status: string): number => {
  switch (status.toLowerCase()) {
    case "unprocessed":
      return 1; // Highest priority
    case "processed":
      return 2; // Lowest priority
    default:
      return 3;
  }
};

export const sortCustomers = (customers: any[]) => {
  return [...customers].sort((a, b) => {
    const statusPriorityA = getStatusPriority(a.status);
    const statusPriorityB = getStatusPriority(b.status);

    // First sort by status priority
    if (statusPriorityA !== statusPriorityB) {
      return statusPriorityA - statusPriorityB;
    }

    // If status is the same, sort by creation date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
};

export const useCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const customersData = await getCustomers();
      setCustomers(customersData as Customer[]);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleStatusUpdate = useCallback(
    async (customerId: string, newStatus: string) => {
      try {
        setUpdatingStatus(customerId);
        const updatedCustomer = await updateCustomerStatus(
          customerId,
          newStatus
        );

        setCustomers((prevCustomers) =>
          prevCustomers.map((customer) =>
            customer.id === customerId ? updatedCustomer : customer
          )
        );
        setEditingStatus(null);
      } catch (error) {
        console.error("Error updating customer status:", error);
        alert("Failed to update customer status. Please try again.");
      } finally {
        setUpdatingStatus(null);
      }
    },
    []
  );

  const toggleStatusEdit = useCallback(
    (customerId: string) => {
      setEditingStatus(editingStatus === customerId ? null : customerId);
    },
    [editingStatus]
  );

  const handleDeleteClick = useCallback((customerId: string) => {
    setShowDeleteConfirm(customerId);
  }, []);

  const handleDeleteConfirm = useCallback(async (customerId: string) => {
    try {
      setDeletingCustomer(customerId);
      await deleteCustomer(customerId);

      setCustomers((prevCustomers) =>
        prevCustomers.filter((customer) => customer.id !== customerId)
      );
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting customer:", error);
      alert("Failed to delete customer. Please try again.");
    } finally {
      setDeletingCustomer(null);
    }
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(null);
  }, []);

  const sortedCustomers = sortCustomers(customers);

  return {
    customers: sortedCustomers,
    loading,
    updatingStatus,
    editingStatus,
    deletingCustomer,
    showDeleteConfirm,
    handleStatusUpdate,
    toggleStatusEdit,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    refreshCustomers: fetchCustomers,
  };
};
